import './diffComponent.scss'
import { Box, Button, Fab, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary } from '../common/Accordion/Accordion'
import { buildTimeStringFromTimestamp } from '../common/time/timeHelper'
import { useTranslation } from 'react-i18next'
import html2pdf from 'html2pdf.js'
import { useSelector } from 'react-redux'
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown'
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
const deepEqual = require('deep-equal')

// Clean keys
function deleteUnwantedKeys(obj, reportType) {
  const unwantedKeys = [
    '__v',
    '_id',
    reportType !== 'abm_users' ? 'email' : '',
    'password',
    'hasApikey',
    'deleted',
    'passwordLastChanged',
    'previousPasswords',
    'emailClient',
    'createdAt',
    'updatedAt',
    'createdUser',
    'updatedUser',
    'apiKey',
    'globalPrexi',
    'ruleTemplate',
    'filterTemplate',
  ]

  const cleanedObject = Object.assign({}, obj)
  for (const key of unwantedKeys) {
    delete cleanedObject[key]
  }

  return cleanedObject
}

// Deep object/arr differenciation
function deepObjectDiff(previous, newImage) {
  const diff = []

  function deepArrayEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) return false

    const sortedArr1 = JSON.stringify(arr1.slice().sort())
    const sortedArr2 = JSON.stringify(arr2.slice().sort())

    return sortedArr1 === sortedArr2
  }

  function compareSubServices(prevSubServices, newSubServices) {
    prevSubServices.forEach((prevSubService) => {
      const newSubService = newSubServices.find((subService) => subService.name === prevSubService.name)

      if (!newSubService) {
        diff.push({
          key: 'subService',
          action: 'deleted',
          data: prevSubService,
        })
      }
    })

    newSubServices.forEach((newSubService) => {
      if (!prevSubServices.some((subService) => subService.name === newSubService.name)) {
        diff.push({
          key: 'subService',
          action: 'added',
          data: newSubService,
        })
      }
    })
  }

  function compareServicesAndSubservices(prevServices, newServices) {
    prevServices.forEach((prevService) => {
      const newService = newServices.find((service) => service.name === prevService.name)

      if (!newService) {
        diff.push({
          key: 'services',
          action: 'deleted',
          data: prevService,
        })
      } else {
        const isServiceUpdated =
          prevService.status !== newService.status || !deepArrayEqual(prevService.subServices, newService.subServices)

        if (isServiceUpdated) {
          diff.push({
            key: 'services',
            action: 'updated',
            data: {
              name: newService.name,
              status: newService.status,
            },
          })
        }

        compareSubServices(prevService.subServices, newService.subServices)
      }
    })

    newServices.forEach((newService) => {
      if (!prevServices.some((service) => service.name === newService.name)) {
        diff.push({
          key: 'services',
          action: 'added',
          data: newService,
        })
      }
    })
  }

  function isObject(val) {
    return val != null && typeof val === 'object' && !Array.isArray(val)
  }

  for (const key in previous) {
    if (!newImage?.hasOwnProperty(key)) {
      diff.push({
        key: key,
        action: 'deleted',
        data: previous[key],
      })
    } else if (Array.isArray(previous[key]) && Array.isArray(newImage[key])) {
      if (key === 'services') {
        // If object have services and subservices (e.g. abm_roles)
        compareServicesAndSubservices(previous[key], newImage[key])
      } else {
        if (!deepEqual(previous[key], newImage[key], { strict: true })) {
          diff.push({
            key: key,
            action: 'updated',
            data: newImage[key],
          })
        }
      }
    } else if (isObject(previous[key]) && isObject(newImage[key])) {
      const nestedDiff = deepObjectDiff(previous[key], newImage[key])
      if (Object.keys(nestedDiff).length > 0) {
        diff.push({
          key: key,
          action: 'updated',
          data: nestedDiff,
        })
      }
    } else if (!deepEqual(previous[key], newImage[key])) {
      diff.push({
        key: key,
        action: 'updated',
        data: `${previous[key]} --> ${newImage[key]}`,
      })
    }
  }

  for (const key in newImage) {
    if (!previous?.hasOwnProperty(key)) {
      diff.push({
        key: key,
        action: 'added',
        data: newImage[key],
      })
    }
  }

  return groupedData(diff)
}

// Function to group information by added, updated and deleted
function groupedData(obj) {
  const groupedData = {}
  obj.forEach((item) => {
    const action = item.action
    if (!groupedData[action]) {
      groupedData[action] = []
    }
    groupedData[action].push(item)
  })

  return groupedData
}

export const generatePDF = (idToConvert, name, email) => {
  const element = document.getElementById(idToConvert) // ID of the HTML element
  const options = {
    margin: [20, 15],
    filename: `${name}_${new Date().getTime()}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2, letterRendering: true },
    jsPDF: { unit: 'pt', format: 'a4', orientation: 'landscape' },
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
  }
  const today = new Date()
  html2pdf()
    .set(options)
    .from(element)
    .toPdf()
    .get('pdf')
    .then((pdf) => {
      let totalPages = pdf.internal.getNumberOfPages()

      for (let i = 1; i <= totalPages; i++) {
        // set footer to every page
        pdf.setPage(i)
        // set footer font
        pdf.setFontSize(10)
        pdf.setTextColor(150)
        // this example gets internal pageSize just as an example to locate your text near the borders in case you want to do something like "Page 3 out of 4"
        if (i === 1) {
          pdf.text(
            // pdf.internal.pageSize.getWidth() - 580,
            pdf.internal.pageSize.getWidth() - 825,
            // pdf.internal.pageSize.getHeight() - 825,
            pdf.internal.pageSize.getHeight() - 580,
            `Güeno - PDF Generated on ${buildTimeStringFromTimestamp(today)} by ${email} - ${today.getTime()}`,
          )
        }
        pdf.text(
          pdf.internal.pageSize.getWidth() - 60,
          pdf.internal.pageSize.getHeight() - 10,
          `Page ${i} of ${totalPages}`,
        )
      }
    })
    .save()
}

function DiffComponent({ previousData, newData, reportKey, info, handleClose }) {
  const { email } = useSelector((state) => state.login)
  const [expanded, setExpanded] = useState([true, true])
  const [userId, setUserId] = useState('')
  const [ruleId, setRuleId] = useState('')
  const [ruleInstanceId, setRuleInstanceId] = useState('')
  const { t } = useTranslation(['common'])

  const handleChange = (position) => {
    let aux = [...expanded]
    aux[position] = !aux[position]
    setExpanded([...aux])
  }

  // Delete unwanted keys
  const previous = deleteUnwantedKeys(previousData, reportKey)
  const newImage = deleteUnwantedKeys(newData, reportKey)

  // Get differences between objects
  const diffResults = deepObjectDiff(previous, newImage)

  const getColorByAction = (action) => {
    switch (action) {
      case 'added':
        return 'green'
      case 'updated':
        return 'orange'
      case 'deleted':
        return 'red'
      default:
        return 'black'
    }
  }

  // Render data
  const renderData = (data) => {
    if (Array.isArray(data)) {
      return (
        <ul style={{ paddingInlineStart: '0px' }}>
          {data.map((item, idx) => {
            return <Box key={idx}>{renderData(item)}</Box>
          })}
        </ul>
      )
    } else if (typeof data === 'object' && data !== null) {
      return (
        <ul>
          {Object.entries(data).map(([key, value], idx) => {
            return (
              <li key={idx} style={{ listStyle: 'circle' }}>
                <strong>{key}:</strong> {renderData(value)}
              </li>
            )
          })}
        </ul>
      )
    } else {
      return (
        <Box className="render-data-wrap-text">
          <span>{data.toString()}</span>
        </Box>
      )
    }
  }

  useEffect(() => {
    if (previousData?.userId || newData?.userId) {
      setUserId(previousData?.userId ?? newData?.userId)
    }
    if (previousData?.ruleId || newData?.ruleId) {
      setRuleId(previousData?.ruleId ?? newData?.ruleId)
    }
    if (previousData?.instanceId || newData?.instanceId) {
      setRuleInstanceId(previousData?.instanceId ?? newData?.instanceId)
    }
  }, [previousData, newData])

  return (
    <Box className="report-modal-flex report-modal-overflow">
      <Box className="modal-top">
        <Button
          onClick={() => generatePDF('contentToConvertReport', reportKey, email)}
          variant="contained"
          size="small"
          color="primary"
        >
          {t('common:exportToPdf')}
        </Button>
        <Fab variant="close" onClick={handleClose}>
          <CloseOutlinedIcon />
        </Fab>
      </Box>
      <Box className="report-modal-flex" id="contentToConvertReport">
        {info && (
          <Paper elevation={0} variant="rootOutlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    {t('common:action')} {t('common:owner')}
                  </TableCell>
                  <TableCell>{t('common:action')}</TableCell>
                  <TableCell>{t('common:status')}</TableCell>
                  <TableCell>{t('common:date')}</TableCell>
                  {userId !== '' && <TableCell>{t('common:userId')}</TableCell>}
                  {ruleId !== '' && <TableCell>{t('common:ruleId')}</TableCell>}
                  {ruleId !== '' && <TableCell>{t('common:ruleInstanceId')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{info.email}</TableCell>
                  <TableCell>{info.action}</TableCell>
                  <TableCell>{info.result}</TableCell>
                  <TableCell>{buildTimeStringFromTimestamp(info.date)}</TableCell>
                  {userId !== '' && <TableCell>{userId}</TableCell>}
                  {ruleId !== '' && <TableCell>{ruleId}</TableCell>}
                  {ruleInstanceId !== '' && <TableCell>{ruleInstanceId}</TableCell>}
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        )}
        <Box className="diff-flex">
          <Paper elevation={0} variant="rootOutlined" className="diff-column">
            <Typography variant="title">{t('common:modifications')}</Typography>
            {/* UPDATED ITEMS */}
            {diffResults && diffResults?.updated?.length && (
              <Box>
                <Typography variant="title4">{t('common:updated')}:</Typography>
                {diffResults?.updated?.map((diffResult, index) => (
                  <Box key={index}>
                    <li
                      key={index}
                      style={{ color: getColorByAction(diffResult.action), marginLeft: '1rem', textWrap: 'nowrap' }}
                    >
                      <strong>{diffResult.key}: </strong>
                      {diffResult.data && renderData(diffResult.data)}
                    </li>
                  </Box>
                ))}
              </Box>
            )}

            {/* DELETED ITEMS */}
            {diffResults && diffResults?.deleted?.length && (
              <Box>
                <Typography variant="title4">{t('common:deleted')}:</Typography>
                {diffResults?.deleted?.map((diffResult, index) => (
                  <Box key={index}>
                    <li key={index} style={{ color: getColorByAction(diffResult.action), marginLeft: '1.5rem' }}>
                      <strong>{diffResult.key}: </strong>
                      {diffResult.data && renderData(diffResult.data, true)}
                    </li>
                  </Box>
                ))}
              </Box>
            )}

            {/* ADDED ITEMS */}
            {diffResults && diffResults?.added?.length && (
              <Box>
                <Typography variant="title4">{t('common:added')}:</Typography>
                {diffResults?.added?.map((diffResult, index) => (
                  <Box key={index}>
                    <li key={index} style={{ color: getColorByAction(diffResult.action), marginLeft: '1.5rem' }}>
                      <strong>{diffResult.key}: </strong>
                      {diffResult.data && renderData(diffResult.data)}
                    </li>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          <Box>
            <Accordion variant="outlined" expanded={expanded[0]} onChange={() => handleChange(0)}>
              <AccordionSummary expandIcon={null}>
                <Box className="accordion-wrapper">
                  <Typography variant="title">{t('common:previousImage')}</Typography>
                  <Button
                    variant="outlinedBlank"
                    onChange={(e) => {
                      e.stopPropagation()
                      return handleChange(0)
                    }}
                  >
                    {expanded[0] ? <KeyboardDoubleArrowDownIcon /> : <KeyboardDoubleArrowUpIcon />}
                  </Button>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {Object.keys(previous).length ? renderData(previous) : t('common:noPreviousData')}
              </AccordionDetails>
            </Accordion>
          </Box>
          <Box>
            <Accordion variant="outlined" expanded={expanded[1]} onChange={() => handleChange(1)}>
              <AccordionSummary expandIcon={null}>
                <Box className="accordion-wrapper">
                  <Typography variant="title">{t('common:newImage')}</Typography>
                  <Button
                    variant="outlinedBlank"
                    onChange={(e) => {
                      e.stopPropagation()
                      return handleChange(1)
                    }}
                  >
                    {expanded[1] ? <KeyboardDoubleArrowDownIcon /> : <KeyboardDoubleArrowUpIcon />}
                  </Button>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {Object.keys(newImage).length ? renderData(newImage) : t('common:noNewData')}
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default DiffComponent
